import Image from 'next/image';
import styled, { css } from 'styled-components';

import useDevice from '@hooks/useDevice';
import { device } from '@styles/mediaQuery';
import { Colors } from '@shared/interfaces/themes';

type SectionHeaderProps = {
  gradient?: boolean;
  align?: 'center' | 'flex-start' | 'flex-end';
  justifyContent?: 'center' | 'flex-start' | 'flex-end';
  title?: string;
  margin?: string;
  src: string;
  height?: number;
  width?: number;
  titleColor?: keyof Colors;
  titleFontSize?: string;
  titleFontWeight?: number;
};

function SectionHeader({
  title = '',
  src,
  gradient,
  align,
  justifyContent,
  width,
  margin,
  height,
  titleColor,
  titleFontSize,
  titleFontWeight,
}: SectionHeaderProps) {
  const { isMobile } = useDevice();

  const size = isMobile ? 14 : 24;

  const splitTitle = title.split(' ');
  const lastWord = [...splitTitle].reverse()[0];
  const filteredTitle = splitTitle
    .filter((chars) => chars !== lastWord)
    .join(' ');

  return (
    <StyledP $margin={margin} $align={align} $justifyContent={justifyContent}>
      <Image
        height={height ?? size}
        width={width ?? size}
        alt={title}
        src={src}
        priority
      />
      {!gradient ? (
        <StyledTitle
          $titleColor={titleColor}
          $titleFontSize={titleFontSize}
          $titleFontWeight={titleFontWeight}
        >
          {title}
        </StyledTitle>
      ) : (
        <StyledTitle>
          {filteredTitle} <LinearSpan>{lastWord}</LinearSpan>
        </StyledTitle>
      )}
    </StyledP>
  );
}

const StyledP = styled.p<{
  $align?: string;
  $justifyContent?: string;
  $margin?: string;
}>`
  width: 100%;
  display: flex;

  margin: ${({ $margin }) => $margin};
  align-items: ${({ $align }) => ($align ? $align : 'center')};
  justify-content: ${({ $justifyContent }) =>
    $justifyContent ? $justifyContent : 'center'};
`;

const StyledTitle = styled.span<{
  $titleColor?: keyof Colors;
  $titleFontSize?: string;
  $titleFontWeight?: number;
}>`
  margin-left: 7px;

  font-size: ${({ $titleFontSize }) =>
    $titleFontSize ? $titleFontSize : '14px'};

  font-weight: ${({ $titleFontWeight }) => $titleFontWeight};

  color: ${({ theme, $titleColor }) =>
    $titleColor ? theme.colors[$titleColor] : theme.colors.forestGreen};

  ${device.tablet(css<{ $titleFontSize?: string }>`
    font-size: ${({ $titleFontSize }) =>
      $titleFontSize ? $titleFontSize : '24px'};
  `)}
`;

const LinearSpan = styled.span`
  mask-image: linear-gradient(to right, #000000ff, #00000000);
`;

export default SectionHeader;
